import React, { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Copy, Trash2, UserPlus, RotateCcw, Mail, Clock, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  useOrganizationMembers,
  useUpdateMemberRole,
  useRemoveMember,
  useOrganizationInvitations,
  useInviteMember,
  useRevokeInvitation,
  useUpdateInvitationRole,
  type OrganizationRole,
} from '@/hooks/useTeamManagement';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useHasFeature, useOrgPlan } from '@/hooks/useOrgPlan';
import { useCanWrite } from '@/hooks/useCanWrite';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import { Link } from 'react-router-dom';
import { Crown, AlertOctagon } from 'lucide-react';

/**
 * Layout shell estratto FUORI dalla function component Members.
 *
 * CRITICO: definirlo DENTRO Members causa un re-mount completo dell'albero
 * figli ad ogni render di Members (es. ogni keystroke nell'input email),
 * perché React identifica il componente per reference. Risultato: focus
 * perso ad ogni lettera digitata → "scrive una lettera alla volta".
 */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SidebarProvider>
    <div className="min-h-screen flex w-full">
      <AppSidebar />
      <SidebarInset className="flex flex-col">
        <Header />
        <main className="flex-1 p-6 md:p-8 space-y-6">
          {children}
        </main>
      </SidebarInset>
    </div>
  </SidebarProvider>
);

const ROLE_OPTIONS: { value: OrganizationRole; label: string; description: string }[] = [
  { value: 'owner',      label: 'Titolare',       description: 'Pieni poteri, gestione billing ed eliminazione organizzazione' },
  { value: 'admin',      label: 'Amministratore', description: 'Gestione team, fornitori, sconti, impostazioni org' },
  { value: 'supervisor', label: 'Supervisore',    description: 'Approva eliminazione progetti/preventivi, gestisce flussi del team' },
  { value: 'technician', label: 'Tecnico',        description: 'Crea e modifica progetti, preventivi e stratigrafie (no eliminazione)' },
  { value: 'viewer',     label: 'Visualizzatore', description: 'Sola lettura su tutto' },
];

const Members: React.FC = () => {
  const { user } = useAuth();
  const { currentRole, currentOrganization } = useCurrentOrganization();
  const canManage = currentRole === 'owner' || currentRole === 'admin';
  const hasMembersFeature = useHasFeature('members');
  const plan = useOrgPlan();
  const { canWrite } = useCanWrite();

  const { data: members = [], isLoading: loadingMembers } = useOrganizationMembers();
  const { data: invitations = [], isLoading: loadingInvites } = useOrganizationInvitations();

  const updateRole         = useUpdateMemberRole();
  const updateInviteRole   = useUpdateInvitationRole();
  const removeMember       = useRemoveMember();
  const inviteMember       = useInviteMember();
  const revokeInvite       = useRevokeInvitation();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrganizationRole>('technician');

  const pendingInvitations = useMemo(
    () => invitations.filter(i => !i.accepted_at && !i.revoked_at && new Date(i.expires_at) > new Date()),
    [invitations],
  );

  const buildInviteUrl = (token: string) => `${window.location.origin}/invite/${token}`;

  const copyInviteLink = (token: string) => {
    navigator.clipboard.writeText(buildInviteUrl(token));
    toast.success('Link copiato negli appunti');
  };

  /** Apre il client email con un mailto: precompilato (oggetto + body con link). */
  const resendInviteByEmail = (email: string, token: string) => {
    const url = buildInviteUrl(token);
    const orgName = currentOrganization?.name ?? 'la nostra organizzazione';
    const subject = `Invito a ${orgName} su DryConfig`;
    const body =
      `Ciao,%0D%0A%0D%0Asei stato invitato a unirti a ${orgName} su DryConfig.%0D%0A%0D%0A` +
      `Apri questo link per accettare l'invito:%0D%0A${url}%0D%0A%0D%0A` +
      `Il link scade tra 7 giorni.%0D%0A%0D%0AGrazie.`;
    window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  /** Giorni residui prima della scadenza. Negativo se gia' scaduto. */
  const daysUntilExpiry = (expiresAt: string): number => {
    const ms = new Date(expiresAt).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const formatExpiry = (expiresAt: string) => {
    const days = daysUntilExpiry(expiresAt);
    if (days < 0) return { label: 'Scaduto', expired: true };
    if (days === 0) return { label: 'Scade oggi', expired: false };
    if (days === 1) return { label: 'Scade domani', expired: false };
    return { label: `Scade tra ${days} gg`, expired: false };
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    inviteMember.mutate(
      { email: inviteEmail, role: inviteRole },
      {
        onSuccess: (inv) => {
          const savedEmail = inviteEmail;
          setInviteEmail('');
          setInviteRole('technician');
          // Auto-copy link + apri mailto: per fare passi avanti subito.
          // Nota: senza SMTP custom configurato (F22), l'invio richiede mailto: o
          // copia manuale → l'utente DEVE inoltrare. La toast con bottone Email
          // riporta sempre il link in clipboard come fallback.
          navigator.clipboard.writeText(buildInviteUrl(inv.token));
          toast.success('Invito creato — link copiato negli appunti', {
            description: `Invialo a ${savedEmail} via email, WhatsApp o clicca "Email" sulla riga sotto per aprire il client mail.`,
            duration: 8000,
          });
        },
      },
    );
  };

  if (!currentOrganization) {
    return (
      <Shell>
        <p className="text-muted-foreground">Nessuna organizzazione attiva.</p>
      </Shell>
    );
  }

  // Priorità canWrite > hasMembersFeature.
  // cancelled/past_due → card rossa "Riattiva abbonamento" (independente dal piano).
  // Solo/Studio (no membersFeature, ma canWrite=true) → upsell amber "Passa a Team".
  // Team / Trial → render gestione completa.
  if (!canWrite) {
    return (
      <Shell>
        <Card className="max-w-2xl border-red-300 bg-red-50/60">
          <CardHeader>
            <div className="flex items-center gap-3">
              <AlertOctagon className="h-6 w-6 text-red-600" />
              <CardTitle className="text-red-700">Abbonamento scaduto · Membri non disponibili</CardTitle>
            </div>
            <CardDescription>
              La gestione del team resta accessibile solo con abbonamento attivo. I dati esistenti
              sono ancora visibili in sola lettura, ma non puoi invitare nuovi membri o modificare
              ruoli finché non riattivi l'abbonamento.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild variant="destructive">
              <Link to="/settings/billing">Riattiva abbonamento →</Link>
            </Button>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // Feature-gate piano: la gestione team multi-utente è inclusa solo nel piano Team.
  // Trial vede l'anteprima completa; Solo/Studio vedono solo l'upsell.
  if (!hasMembersFeature) {
    return (
      <Shell>
        <Card className="max-w-2xl border-primary/20 bg-primary/5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Crown className="h-6 w-6 text-primary" />
              <CardTitle>Gestione team (piano Team)</CardTitle>
            </div>
            <CardDescription>
              Il tuo piano attuale è <strong>{plan.label}</strong>. La gestione di più utenti con
              ruoli avanzati è inclusa nel piano <strong>Team €149/mese</strong>: 10 utenti, ruoli
              owner/admin/supervisor/technician/viewer, audit log, e supporto prioritario.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• Inviti illimitati al team</li>
              <li>• 5 ruoli con permessi granulari (chi crea, chi elimina, chi solo guarda)</li>
              <li>• Multi-organizzazione (più cantieri/sedi separate)</li>
              <li>• Supporto prioritario via email + telefono</li>
            </ul>
            <Button asChild>
              <Link to="/settings/billing">Passa a Team — €149/mese →</Link>
            </Button>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-tour-id="members-page">Membri del team</h1>
        <p className="text-muted-foreground text-sm">
          Gestisci chi può accedere a <span className="font-medium">{currentOrganization.name}</span> e con quale ruolo.
        </p>
      </div>

      {/* Banner informativo SMTP — temporaneo fino a F22 (Resend custom domain) */}
      {canManage && (
        <Card className="bg-blue-50/50 border-blue-200">
          <CardContent className="py-3 flex items-start gap-3">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-900 space-y-0.5">
              <p>
                <strong>Invio email non automatico</strong> — fino al rilascio dell'SMTP custom (in
                roadmap), gli inviti vanno inoltrati a mano.
              </p>
              <p className="text-blue-800">
                Quando crei un invito, il link viene <strong>copiato negli appunti</strong>.
                Puoi anche cliccare <strong>"Email"</strong> nella riga sotto per aprire il tuo
                client mail con un messaggio precompilato.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite form (admin/owner only) */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" /> Invita un nuovo membro
            </CardTitle>
            <CardDescription>
              Genera un link di invito da inviare via email/WhatsApp. Scade dopo 14 giorni.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3 items-end">
              <div>
                <Label htmlFor="invite-email">Email destinatario</Label>
                <Input
                  id="invite-email"
                  type="email"
                  required
                  placeholder="nome@azienda.it"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="invite-role">Ruolo</Label>
                <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as OrganizationRole)}>
                  <SelectTrigger id="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>
                        <div className="flex flex-col">
                          <span>{r.label}</span>
                          <span className="text-xs text-muted-foreground">{r.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={inviteMember.isPending}>
                {inviteMember.isPending ? 'Creo...' : 'Crea invito'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Members table */}
      <Card>
        <CardHeader>
          <CardTitle>Membri attivi ({members.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMembers ? (
            <p className="text-muted-foreground text-sm">Caricamento…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Iscritto</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map(m => {
                  const isSelf = m.user_id === user?.id;
                  return (
                    <TableRow key={m.membership_id}>
                      <TableCell>{m.name || '—'} {isSelf && <Badge variant="secondary">Tu</Badge>}</TableCell>
                      <TableCell>{m.email}</TableCell>
                      <TableCell>
                        {canManage && !isSelf ? (
                          <Select
                            value={m.role}
                            onValueChange={(v) => updateRole.mutate({ membershipId: m.membership_id, newRole: v as OrganizationRole })}
                          >
                            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ROLE_OPTIONS.map(r => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge>{ROLE_OPTIONS.find(r => r.value === m.role)?.label ?? m.role}</Badge>
                        )}
                      </TableCell>
                      <TableCell>{new Date(m.joined_at).toLocaleDateString('it-IT')}</TableCell>
                      <TableCell className="text-right">
                        {canManage && !isSelf && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (confirm(`Rimuovere ${m.email} dall'organizzazione?`)) {
                                removeMember.mutate(m.membership_id);
                              }
                            }}
                            title="Rimuovi"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pending invitations */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>Inviti in attesa ({pendingInvitations.length})</CardTitle>
            <CardDescription>
              Per ogni invito copia il link e invialo al destinatario via email o WhatsApp.
              In Fase 2 attiveremo l'invio automatico via email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingInvites ? (
              <p className="text-muted-foreground text-sm">Caricamento…</p>
            ) : pendingInvitations.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nessun invito pendente.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Ruolo</TableHead>
                    <TableHead>Scade</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingInvitations.map(i => {
                    const exp = formatExpiry(i.expires_at);
                    return (
                      <TableRow key={i.id}>
                        <TableCell className="font-medium">{i.email}</TableCell>
                        <TableCell>
                          <Select
                            value={i.role}
                            onValueChange={(v) => updateInviteRole.mutate({
                              invitationId: i.id,
                              newRole: v as OrganizationRole,
                            })}
                          >
                            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ROLE_OPTIONS.map(r => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center gap-1 text-xs ${
                              exp.expired
                                ? 'text-red-700'
                                : daysUntilExpiry(i.expires_at) <= 1
                                  ? 'text-amber-700'
                                  : 'text-muted-foreground'
                            }`}
                            title={new Date(i.expires_at).toLocaleString('it-IT')}
                          >
                            {exp.expired ? <AlertCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                            {exp.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resendInviteByEmail(i.email, i.token)}
                            title="Apri client email con link precompilato"
                          >
                            <Mail className="h-4 w-4 mr-1" /> Email
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => copyInviteLink(i.token)}>
                            <Copy className="h-4 w-4 mr-1" /> Copia link
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Revoca invito"
                            onClick={() => {
                              if (confirm(`Revocare l'invito a ${i.email}?`)) {
                                revokeInvite.mutate(i.id);
                              }
                            }}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </Shell>
  );
};

export default Members;
